import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, LockKeyholeOpen, Pencil, Trash2, UserPlus, X } from "lucide-react";
import api from "../utils/axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { DESIGNATION_LABELS, formatDesignation } from "../utils/designations";

// Canonical order designations are stored/joined in (matches DESIGNATION_LABELS keys)
const ALL_DESIGNATIONS = ["Admin", "CSM", "CM", "Manager", "Client"];

// "Admin,CSM" -> ["Admin", "CSM"]; also tolerates a single plain value
const parseDesignations = (value = "") =>
  String(value).split(",").map((s) => s.trim()).filter(Boolean);

// Keeps them in ALL_DESIGNATIONS order regardless of selection order, then joins
const joinDesignations = (list = []) =>
  ALL_DESIGNATIONS.filter((d) => list.includes(d)).join(",");

// Reusable checkbox group used by both the Add and Edit modals
function DesignationCheckboxes({ selected, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-muted-foreground">Designation(s)</label>
      {ALL_DESIGNATIONS.map((code) => (
        <label key={code} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
          <input
            type="checkbox"
            checked={selected.includes(code)}
            onChange={(e) => {
              onChange(
                e.target.checked
                  ? [...selected, code]
                  : selected.filter((d) => d !== code)
              );
            }}
          />
          <span>{DESIGNATION_LABELS[code]}</span>
        </label>
      ))}
    </div>
  );
}

export default function UserManagement() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    designations: [],
  });

  const [editForm, setEditForm] = useState({ id: null, name: "", email: "", designations: [] });
  const [editSaving, setEditSaving] = useState(false);

  const [newPassword, setNewPassword] = useState("");

  async function loadUsers() {
    setLoading(true);
    const res = await api.get("/auth/all-users");
    setUsers(res.data);
    setLoading(false);
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function handleAddUser(e) {
    e.preventDefault();
    if (form.designations.length === 0) {
      alert("Select at least one designation");
      return;
    }
    try {
      const res = await api.post("/users", {
        name: form.name,
        email: form.email,
        password: form.password,
        designation: joinDesignations(form.designations),
      });
      if (res.data.ok) {
        alert("User created successfully");
        setShowAddModal(false);
        setForm({ name: "", email: "", password: "", designations: [] });
        loadUsers();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add user");
    }
  }

  function openEditModal(u) {
    setEditForm({
      id: u.id,
      name: u.name,
      email: u.email,
      designations: parseDesignations(u.designation),
    });
    setShowEditModal(true);
  }

  async function handleEditUser(e) {
    e.preventDefault();
    if (editForm.designations.length === 0) {
      alert("Select at least one designation");
      return;
    }
    setEditSaving(true);
    try {
      const res = await api.put(`/users/${editForm.id}`, {
        name: editForm.name,
        email: editForm.email,
        designation: joinDesignations(editForm.designations),
      });
      if (res.data.ok) {
        alert("User updated successfully");
        setShowEditModal(false);
        loadUsers();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update user");
    } finally {
      setEditSaving(false);
    }
  }

  async function handlePasswordUpdate(e) {
    e.preventDefault();
    try {
      const res = await api.put(
        `/users/${selectedUser.id}/password`,
        { password: newPassword }
      );

      if (res.data.ok) {
        alert("Password updated");
        setShowPassModal(false);
        setNewPassword("");
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update password");
    }
  }

  async function deleteUser(id) {
    if (!window.confirm("Are you sure?")) return;
    try {
      await api.delete(`/users/${id}`);
      loadUsers();
    } catch {
      alert("Error deleting user");
    }
  }

  const logout = () => {
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  return (
    <div className="mx-auto px-6 py-8">
      <Navbar logout={logout} />

      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold">User Management</h1>

          <div className="flex gap-3">
            <Button variant="outline" className="neon-left" asChild>
              <Link to="/">Back</Link>
            </Button>

            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-neon text-black hover:bg-neon/90"
            >
              <UserPlus size={18} />
              Add User
            </Button>
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-2xl neon-left neon-table overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 text-sm font-medium bg-black/5 dark:bg-white/5">
            <div className="col-span-3">Name</div>
            <div className="col-span-4">Email ID</div>
            <div className="col-span-3">Designation</div>
            <div className="col-span-2 text-center">Action</div>
          </div>

          {loading ? (
            <div className="p-6 ">Loading...</div>
          ) : (
            users.map((u) => (
              <div
                key={u.id}
                className="grid grid-cols-12 px-6 py-4 text-sm border-b border-black/5 hover:bg-black/[0.03] dark:border-white/5 dark:hover:bg-white/[0.03]"
              >
                <div className="col-span-3">{u.name}</div>
                <div className="col-span-4">{u.email}</div>
                <div className="col-span-3">{formatDesignation(u.designation)}</div>

                <div className="col-span-2 flex justify-center gap-4">
                  <LockKeyholeOpen
                    size={18}
                    className="cursor-pointer hover:text-neon"
                    onClick={() => {
                      setSelectedUser(u);
                      setShowPassModal(true);
                    }}
                  />

                  <Pencil
                    size={18}
                    className="cursor-pointer hover:text-neon"
                    onClick={() => openEditModal(u)}
                  />

                  <Trash2
                    size={18}
                    className="cursor-pointer hover:text-red-500"
                    onClick={() => deleteUser(u.id)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ADD USER MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative neon-left neon-table w-96 p-6 rounded-xl max-h-[90vh] overflow-y-auto">
            {/* ❌ Close Button */}
            <button
              onClick={() => setShowAddModal(false)}
              className="absolute top-3 right-3   transition"
            >
              <X size={18} />
            </button>

            <h3 className="text-lg font-semibold mb-4">Add New User</h3>

            <form onSubmit={handleAddUser} className="space-y-3">
              <Input
                placeholder="Full Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              <Input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />

              <Input
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />

              <DesignationCheckboxes
                selected={form.designations}
                onChange={(designations) => setForm({ ...form, designations })}
              />

              <Button className="w-full bg-neon text-black">Add User</Button>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative neon-left neon-table w-96 p-6 rounded-xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-3 right-3 transition"
            >
              <X size={18} />
            </button>

            <h3 className="text-lg font-semibold mb-4">Edit User</h3>

            <form onSubmit={handleEditUser} className="space-y-3">
              <Input
                placeholder="Full Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />

              <Input
                type="email"
                placeholder="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />

              <DesignationCheckboxes
                selected={editForm.designations}
                onChange={(designations) => setEditForm({ ...editForm, designations })}
              />

              <Button className="w-full bg-neon text-black" disabled={editSaving}>
                {editSaving ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* CHANGE PASSWORD MODAL */}
      {showPassModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative neon-left neon-table w-80 p-6 rounded-xl">
            {/* ❌ Close Button */}
            <button
              onClick={() => setShowPassModal(false)}
              className="absolute top-3 right-3  transition"
            >
              <X size={18} />
            </button>

            <h3 className="text-lg font-semibold mb-4">
              Change Password – {selectedUser?.name}
            </h3>

            <form onSubmit={handlePasswordUpdate} className="space-y-3">
              <Input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />

              <Button className="w-full bg-neon text-black">
                Update Password
              </Button>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}